import type {ReactElement, ReactNode} from 'react';
import {useRef, useState} from 'react';
import type {GeneratedImageIconsProps} from './generated-image-actions-icons.types';
import {ChatImageActionPanelId} from './generated-image-actions-icons.types';
import styles from './generated-image-actions-icons.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import {AnimatePresence, motion} from 'framer-motion';
import {isTouchDevice} from '@Utils/browser.util';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {PublishingOptions} from '@Components/ai-chat-window/components/publishing-options/publishing-options';
import {useUpdateMoreOptionsModal} from '@Components/modal/hooks/useUpdateMoreOptionsModal';
import {openMoreOptionsModal} from '@Modals/more-options-modal/more-options-modal-library';
import {useImageEditMenu} from '@Components/ai-chat-window/hooks/useImageEditMenu';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import type {GeneratedImagePartActionsLoading} from '@/ai-chat/providers/ai-chat-provider.types';
import {LoadingStates} from '@Utils/loading.util';
import {AnimationCreationStatus} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import {
  AnimationGenerationErrorCode,
  ensureAnimationCredits,
  generateAnimationForAiChatImage,
  getAnimationErrorMessage,
} from '@Components/ai-chat-window/lib/ai-chat-animation-generation.library';
import type {AnimationGenerationError} from '@Components/ai-chat-window/lib/ai-chat-animation-generation.library';
import {AiChatTurnToVideoSource, AiChatTurnToVideoType, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {markAiChatCreateVideoIntroTileAsSeen} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/assistant-message-image-part-action-tiles.library';
import {ResponseFeedback} from '@Components/ai-chat-window/components/response-feedback/response-feedback';
import {useChangeStyleThemeSelect} from '@Components/ai-chat-window/hooks/useChangeStyleThemeSelect';
import {ChangeStylePopup} from '@Components/ai-chat-window/components/change-style-dropdown/components/change-style-popup/change-style-popup';
import {ThemeGrid} from '@Components/ai-chat-window/components/change-style-dropdown/components/theme-grid/theme-grid';
import {useSaveToMyDesigns} from '@Components/ai-chat-window/hooks/useSaveToMyDesigns';
import {useResetAiActionLoadingStatesOnModalClose} from '@Components/ai-chat-window/hooks/useResetAiActionLoadingStatesOnModalClose';
import {usePublishChatModelGeneratedImage} from '@Components/ai-chat-window/hooks/usePublishChatModelGeneratedImage';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';

export function GeneratedImageActionsIcons({...props}: GeneratedImageIconsProps): ReactNode {
  const {state, actions} = useAiChat();
  const {publishToSocialMedia, publishAsEmailCampaign, promoteAsEvent} = usePublishChatModelGeneratedImage(props.chatId, props.hashedId);
  useResetAiActionLoadingStatesOnModalClose(props.chatId, props.hashedId);

  const [dropdownOpenState, setDropdownOpenState] = useState<ChatImageActionPanelId | null>(null);

  const [keepVisible, setKeepVisible] = useState(false);

  const isTurnIntoVideoInFlightRef = useRef<boolean>(false);

  const {onSaveToMyDesigns} = useSaveToMyDesigns(props.chatId, props.hashedId);

  const {onThemeSelect} = useChangeStyleThemeSelect({
    chatId: props.chatId,
    onGenerate: props.onGenerate,
    imageHashedId: props.hashedId,
  });

  const getLoadingStatesForImageActions = (): GeneratedImagePartActionsLoading => {
    return state.getLoadingStatesForImageActions(props.chatId, props.hashedId);
  };

  const getOpenState = (id: ChatImageActionPanelId): boolean => {
    return dropdownOpenState === id;
  };

  const onOpen = (id: ChatImageActionPanelId): void => {
    setDropdownOpenState(id);
    setKeepVisible(true);
  };

  const onClose = (id: ChatImageActionPanelId): void => {
    if (getOpenState(id)) {
      setDropdownOpenState(null);
      setKeepVisible(false);
    }
  };

  const isDisabled =
    getLoadingStatesForImageActions().publishingLoadingState === LoadingStates.LOADING ||
    getLoadingStatesForImageActions().shareLoadingState === LoadingStates.LOADING ||
    getLoadingStatesForImageActions().saveToMyDesignsLoadingState === LoadingStates.LOADING ||
    getLoadingStatesForImageActions().makeTextEditableLoadingState === LoadingStates.LOADING;

  const imageEditMenu = useImageEditMenu({
    chatId: props.chatId,
    hashedId: props.hashedId,
    hasLayeredPoster: props.hasLayeredPoster,
    moreOptionsPanelId: ChatImageActionPanelId.MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID,
    isDisabled,
  });

  const getDownloadIcon = (): ReactNode => {
    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        overlay={<Tooltip className={`spacing-m-b-1 _rounded-arrow ${isTouchDevice() ? '_invisible' : ''}`}>{window.i18next.t('pmwjs_download')}</Tooltip>}
        delay={{show: 0, hide: 0}}
        placement="top"
        offset={undefined}
      >
        <Icon
          icon="icon-download"
          size={IconSize.SIZE_ICON_20}
          type={IconType.GHOST}
          shape={IconShape.CIRCLE}
          onClick={() => {
            props.onDownload();
          }}
          isDisabled={getLoadingStatesForImageActions().downloadLoadingState === LoadingStates.LOADING}
          isLoading={getLoadingStatesForImageActions().downloadLoadingState === LoadingStates.LOADING}
          className={props.isMobile ? styles.mobileIcon : ''}
          loaderClassName={props.isMobile ? styles.loaderMobileIcon : styles.loaderIcon}
        />
      </OverlayTrigger>
    );
  };

  const getPublishingOptionsComponent = (): ReactElement => {
    return (
      <PublishingOptions
        chatId={props.chatId}
        hashedId={props.hashedId}
        loadingStates={getLoadingStatesForImageActions()}
        publishToSocialMedia={publishToSocialMedia}
        publishAsEmailCampaign={publishAsEmailCampaign}
        promoteAsEvent={promoteAsEvent}
        removeLastItemBottomMargin={false}
      />
    );
  };

  const isChangeStyleDisabled = !props.canWrite || !!props.isMessageStreaming;

  const getChangeStyleIcon = (): ReactNode => {
    const id = ChatImageActionPanelId.CHANGE_STYLE_DROPDOWN_MODAL_PANEL_ID;

    const getIcon = (isDisabled = false): ReactElement => {
      const tooltip = isDisabled ? window.i18next.t('pmwjs_change_style_unavailable_while_generating') : window.i18next.t('pmwjs_change_style');
      return (
        <OverlayTrigger
          trigger={['hover', 'focus']}
          overlay={<Tooltip className={`spacing-m-b-1 _rounded-arrow ${isTouchDevice() ? '_invisible' : ''}`}>{tooltip}</Tooltip>}
          delay={{show: 0, hide: 0}}
          placement="top"
          offset={undefined}
        >
          <Icon
            icon="icon-repeat"
            size={IconSize.SIZE_ICON_20}
            type={IconType.GHOST}
            shape={IconShape.CIRCLE}
            isActive={getOpenState(id)}
            className={props.isMobile ? styles.mobileIcon : ''}
            isDisabled={isDisabled}
          />
        </OverlayTrigger>
      );
    };

    if (isChangeStyleDisabled) {
      return getIcon(isChangeStyleDisabled);
    }

    return (
      <Dropdown
        open={getOpenState(id)}
        key={id}
        id={id}
        onOpen={(): void => {
          onOpen(id);
        }}
        onClose={(): void => {
          onClose(id);
        }}
        isResumable
        doCloseOnInnerClick
        showModalForSmallScreen
        modalTitle={window.i18next.t('pmwjs_ai_chat_change_style_heading')}
        modalContent={<ThemeGrid onThemeSelect={onThemeSelect} />}
        modalClassName={styles.changeStyleModalContent}
        panelIdForModal={ChatImageActionPanelId.CHANGE_STYLE_DROPDOWN_MODAL_PANEL_ID}
        popupClassName={`${styles.actionPopup} ${styles.changeStyle}`}
        alignment={ALIGNMENT_TYPE.RIGHT}
        useDefaultArrow
        arrowClasses={styles.arrow}
        className={styles.positionUnset}
        selectorClassNameOnOpen={'-active'}
        selector={<div>{getIcon()}</div>}
        popup={
          <ChangeStylePopup>
            <ThemeGrid onThemeSelect={onThemeSelect} />
          </ChangeStylePopup>
        }
      />
    );
  };

  const openChangeStyleModal = (): void => {
    openMoreOptionsModal({
      headerTitle: window.i18next.t('pmwjs_ai_chat_change_style_heading'),
      content: <ThemeGrid onThemeSelect={onThemeSelect} />,
      panelId: ChatImageActionPanelId.CHANGE_STYLE_DROPDOWN_MODAL_PANEL_ID,
      className: styles.changeStyleModalContent,
      shouldCenterAlignTitle: true,
      showCloseButton: true,
    });
  };

  const getCompactMenuRows = (): ReactNode => {
    if (!props.isCompact) {
      return null;
    }

    return (
      <>
        <ControlledListItemType7
          id={'download-option'}
          text={window.i18next.t('pmwjs_download')}
          icon={'icon-download'}
          onClick={props.onDownload}
          className={'radius-8'}
          isLoading={getLoadingStatesForImageActions().downloadLoadingState === LoadingStates.LOADING}
          isDisabled={getLoadingStatesForImageActions().downloadLoadingState === LoadingStates.LOADING}
        />
        <ControlledListItemType7 id={'change-style-option'} text={window.i18next.t('pmwjs_change_style')} icon={'icon-repeat'} onClick={openChangeStyleModal} className={'radius-8'} isDisabled={isChangeStyleDisabled} />
      </>
    );
  };

  const moreOptionsDropdownPopup = (): ReactElement => {
    if (imageEditMenu.isEditMenuInModalMode && imageEditMenu.isEditSubmenuScreen) {
      return imageEditMenu.getEditSubmenu();
    }

    return (
      <>
        {getCompactMenuRows()}
        {imageEditMenu.getEditMenuRow()}
        {getPublishingOptionsComponent()}
        <ControlledListItemType7
          id={'save-to-my-designs'}
          text={window.i18next.t('pmwjs_save_to_my_designs')}
          icon={'icon-folder-2'}
          onClick={() => {
            void onSaveToMyDesigns();
          }}
          className={'radius-8'}
          isLoading={getLoadingStatesForImageActions().saveToMyDesignsLoadingState === LoadingStates.LOADING}
          isDisabled={isDisabled}
        />
        <ControlledListItemType7
          id={'share-design-option'}
          text={window.i18next.t('pmwjs_share_with_others')}
          icon={'icon-group-add'}
          onClick={() => {
            void props.onShareDesign();
          }}
          className={'radius-8 _unmargin'}
          isLoading={getLoadingStatesForImageActions().shareLoadingState === LoadingStates.LOADING}
          isDisabled={isDisabled}
        />
      </>
    );
  };

  useUpdateMoreOptionsModal(
    moreOptionsDropdownPopup(),
    [
      dropdownOpenState,
      isDisabled,
      props.isCompact,
      imageEditMenu.editScreen,
      getLoadingStatesForImageActions().shareLoadingState,
      getLoadingStatesForImageActions().saveToMyDesignsLoadingState,
      getLoadingStatesForImageActions().makeTextEditableLoadingState,
      getLoadingStatesForImageActions().makeDesignEditableLoadingState,
      getLoadingStatesForImageActions().loadingPublishingOption,
    ],
    {panelIdOpenedInModal: ChatImageActionPanelId.MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID, additionalPanelProps: imageEditMenu.getMoreOptionsModalHeaderProps()}
  );

  const getTurnIntoVideoIcon = (): ReactNode => {
    if (!state.isAiChatCreateAVideoFeatureEnabled) {
      return null;
    }
    const isGeneratingVideo = state.getAnimationCreationState(props.chatId, props.hashedId).status === AnimationCreationStatus.CREATING;

    const runTurnIntoVideo = async (): Promise<void> => {
      const hasCredits = await ensureAnimationCredits(state.aiChatGenerateAudio);
      if (!hasCredits) {
        isTurnIntoVideoInFlightRef.current = false;
        return;
      }

      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TURNTOVIDEO, {
        [GA4EventParamName.SOURCE]: AiChatTurnToVideoSource.DESIGN,
        [GA4EventParamName.TYPE]: state.aiChatGenerateAudio ? AiChatTurnToVideoType.WITH_AUDIO : AiChatTurnToVideoType.SILENT,
      });

      const abortController = new AbortController();
      actions.updateActionTileStates({isCreateAVideoFeatureIntroVisible: false});
      void markAiChatCreateVideoIntroTileAsSeen();
      actions.updateAnimationCreationState(props.chatId, props.hashedId, {
        isVisible: true,
        status: AnimationCreationStatus.CREATING,
        abortController,
      });

      try {
        const animation = await generateAnimationForAiChatImage(props.hashedId, {
          signal: abortController.signal,
          onPollingKeyReceived: (pollingKey: string): void => {
            actions.updateAnimationCreationState(props.chatId, props.hashedId, {pollingKey});
          },
          generateAudio: state.aiChatGenerateAudio,
        });
        actions.updateAnimationCreationState(props.chatId, props.hashedId, {
          status: AnimationCreationStatus.COMPLETED,
          animations: [animation],
          abortController: undefined,
          pollingKey: undefined,
        });
        actions.refreshCredits?.();
        isTurnIntoVideoInFlightRef.current = false;
      } catch (e) {
        if ((e as AnimationGenerationError).code === AnimationGenerationErrorCode.INSUFFICIENT_CREDITS) {
          actions.updateAnimationCreationState(props.chatId, props.hashedId, {
            isVisible: false,
            status: AnimationCreationStatus.IDLE,
            abortController: undefined,
            pollingKey: undefined,
          });
          const hasPurchasedCredits = await ensureAnimationCredits(state.aiChatGenerateAudio);
          isTurnIntoVideoInFlightRef.current = false;
          if (hasPurchasedCredits) {
            handleClick();
          }
          return;
        }

        if (!abortController.signal.aborted) {
          actions.updateAnimationCreationState(props.chatId, props.hashedId, {
            status: AnimationCreationStatus.FAILED,
            errorMessage: getAnimationErrorMessage((e as AnimationGenerationError).code),
            abortController: undefined,
            pollingKey: undefined,
          });
          actions.refreshCredits?.();
        }
        isTurnIntoVideoInFlightRef.current = false;
      }
    };

    const handleClick = (): void => {
      if (!props.canWrite || isGeneratingVideo || isTurnIntoVideoInFlightRef.current) {
        return;
      }
      isTurnIntoVideoInFlightRef.current = true;
      void runTurnIntoVideo();
    };

    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        overlay={<Tooltip className={`spacing-m-b-1 _rounded-arrow ${isTouchDevice() ? '_invisible' : ''}`}>{window.i18next.t('pmwjs_turn_into_video')}</Tooltip>}
        delay={{show: 0, hide: 0}}
        placement="top"
        offset={undefined}
      >
        <Icon
          icon="icon-video-ai"
          size={IconSize.SIZE_ICON_20}
          type={IconType.GHOST}
          shape={IconShape.CIRCLE}
          isDisabled={!props.canWrite || isGeneratingVideo}
          onClick={handleClick}
          className={props.isMobile ? styles.mobileIcon : ''}
        />
      </OverlayTrigger>
    );
  };

  const getMoreOptionsIcon = (): ReactNode => {
    const id = ChatImageActionPanelId.MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID;

    return (
      <Dropdown
        open={getOpenState(id)}
        key={id}
        id={id}
        onOpen={(): void => {
          onOpen(id);
        }}
        onClose={(): void => {
          onClose(id);
        }}
        doCloseOnInnerClick={true}
        showModalForSmallScreen
        modalTitle={window.i18next.t('pmwjs_more_options')}
        panelIdForModal={ChatImageActionPanelId.MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID}
        popupClassName={styles.actionPopup}
        selectorClassNameOnOpen={'-active'}
        className={styles.positionUnset}
        alignment={ALIGNMENT_TYPE.RIGHT}
        useDefaultArrow
        selector={
          <Icon
            isDisabled={!props.canWrite}
            icon="icon-more-h"
            size={IconSize.SIZE_ICON_20}
            type={IconType.GHOST}
            shape={IconShape.CIRCLE}
            className={props.isMobile ? styles.mobileIcon : ''}
          />
        }
        popup={moreOptionsDropdownPopup()}
      />
    );
  };

  const getStripOnlyIcons = (): ReactNode => {
    if (props.isCompact) {
      return null;
    }

    return (
      <>
        {getChangeStyleIcon()}
        {getDownloadIcon()}
      </>
    );
  };

  const getResponseFeedback = (): ReactElement => {
    return (
      <ResponseFeedback
        areFeedbackOptionsDisabled={!props.canWrite}
        chatId={props.chatId}
        message={props.message}
        messages={props.messages}
        isNegativeFeedbackTextInputPopupOpen={getOpenState(ChatImageActionPanelId.NEGATIVE_FEEDBACK_DROPDOWN_MODAL_PANEL_ID)}
        onOpenNegativeFeedbackTextInputPopup={() => {
          onOpen(ChatImageActionPanelId.NEGATIVE_FEEDBACK_DROPDOWN_MODAL_PANEL_ID);
        }}
        onCloseNegativeFeedbackTextInputPopup={() => {
          onClose(ChatImageActionPanelId.NEGATIVE_FEEDBACK_DROPDOWN_MODAL_PANEL_ID);
        }}
        popupClassname={`${styles.actionPopup} ${styles.wider}`}
        isMobile={props.isMobile}
        containerClassName={'gap-1 flex-v-row'}
        dropdownPopupAlignment={ALIGNMENT_TYPE.RIGHT}
        onRegenerate={props.onRegenerate}
        onGenerate={props.onGenerate}
        onOpenInEditor={props.onOpenPosterInEditor}
        imageHashedId={props.hashedId}
        canWrite={props.canWrite}
        isMessageStreaming={props.isMessageStreaming}
      />
    );
  };

  return (
    <AnimatePresence>
      {props.isVisible || keepVisible || isTouchDevice() ? (
        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0, display: 'none'}}
          transition={{duration: 0.2, ease: 'easeInOut'}}
          className={`flexbox gap-1 flex-v-row ${styles.imageActions} _position-relative`}
        >
          {getResponseFeedback()}
          {getTurnIntoVideoIcon()}
          {getStripOnlyIcons()}
          {getMoreOptionsIcon()}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
